<template>
    <div style="height: 7px;" :class="{ 'grey4-grey4-background': listStore.cartLists.length > 0 || isScanAvailable }">
        <ion-progress-bar class="max-height" v-if="loading" type="indeterminate" color="medium"></ion-progress-bar>
    </div>

    <ion-row v-if="isScanAvailable" class="grey4-grey4-background" @click="chooseListId">
        <ion-col class="d-flex center-y px-3">
            <ion-img
                src="/assets/icon/barcode.svg"
                class="scan-icon"
            />
            <ion-text class="fs-smaller purple-purple-color ml-1">
                {{ $t('messages.addProductsViaScanner') }}
            </ion-text>
            <select-simple
                id="list-select"
                v-model="scanSupplier"
                :options="supplierStore.allSuppliers.filter(supplier => supplier.scannerActivated)"
                @change="openScanModel"
            >
                <template #options>
                    <ion-select-option
                        v-for="supplier of supplierStore.allSuppliers.filter(supplier => supplier.scannerActivated)"
                        :key="supplier.id"
                        :value="supplier"
                    >
                        {{ supplier.name }}
                    </ion-select-option>
                </template>
            </select-simple>
        </ion-col>
    </ion-row>

    <div class="max-height d-flex flex-d-c max-width">
        <ion-grid
            v-if="!userStore.sessionInfo.emailConfirmed" style="color: darkgoldenrod;"
            class="grey4-grey4-background pb-1 flex-grow-0 max-width"
        >
            <ion-row>
                <ion-col size="2" class="center">
                    <ion-icon class="fs-28" :icon="warningOutline" />
                </ion-col>
                <ion-col>
                    {{ $t('messages.register.validateEmail') }}
                </ion-col>
            </ion-row>

            <ion-row>
                <ion-col class="center">
                    <u @click="resendMail">{{ $t('button.resend') }}</u>
                </ion-col>
            </ion-row>
        </ion-grid>

        <ion-accordion-group
            v-if="listStore.cartLists.length > 0"
            :value="openList"
            multiple
        >
            <div
                v-for="cartList of listStore.cartLists"
                :key="cartList.supplier.id"
                class="cart mt-1"
            >
                <ion-accordion
                    :value="cartList.supplier.id"
                    class="light-light-background"
                >
                    <ion-item
                        slot="header"
                        class="grey4-grey4-background accordion-90-deg cart-header"
                        lines="none"
                        @click.stop="toggleAccordion(cartList.supplier.id)"
                    >
                        <ion-avatar slot="start" style="min-height: 64px; min-width: 64px; margin-top: 5px;">
                            <img
                                v-if="cartList.supplier.imageLink" alt=""
                                :src="(cartList.supplier.imageLink.startsWith('/')) ? `https://app.octopusorder.com${cartList.supplier.imageLink}` : cartList.supplier.imageLink"
                                class="card-border max-height of-contain"
                            />
                            <ion-icon
                                v-else-if="cartList.supplier.iconType"
                                :src="`assets/icon/${cartList.supplier.iconType}.svg`"
                                class="max-size purple-purple-color"
                            />
                            <ion-icon v-else src="assets/icon/deliverer.svg" class="max-size purple-purple-color" />
                        </ion-avatar>

                        <ion-label style="--color: var(--ion-color-purple)">
                            <div class="ion-text-wrap cart-supplier-name">
                                {{ $t('messages.cart.selectionFrom', { deliverer: cartList.supplier.name }) }}
                            </div>
                            <!-- <ion-row v-if="cartList.summary.some((i) => i.isPromotion || i.item.promotion)"> -->
                            <ion-row v-if="showPromoPill(cartList)">
                                <ion-col size="auto" class="pt-1 p-0">
                                    <highlight-box :color="HIGHLIGHT_COLOR.CORAL">
                                        {{ $t(`enumT.${HIGHLIGHT_MESSAGE.PROMOTION}`) }}
                                    </highlight-box>
                                </ion-col>
                            </ion-row>
                        </ion-label>
                    </ion-item>

                    <div slot="content">
                        <ion-grid class="no-padding-grid-only purple-purple-color light-light-background">
                            <ion-row v-if="cartList.lists.length" class="mt-2">
                                <ion-col>
                                    <div class="ml-2 fw-b">{{ $t('messages.cart.fromList') }}:</div>
                                </ion-col>
                                <ion-col size="auto">
                                    <link-text @click="deleteCartCheck(cartList)" class="active-link-click">
                                        {{ $t('button.deleteSupplierCart') }}
                                    </link-text>
                                </ion-col>
                            </ion-row>

                            <template v-for="list of cartList.lists" :key="list.name">
                                <ion-row class="my-1">
                                    <ion-col size="auto" class="center-y px-1">
                                        <div class="purple-purple-color" style="height: 50px; width: 50px;">
                                            <ion-icon src="assets/icon/list.svg" class="max-size" />
                                        </div>
                                    </ion-col>

                                    <ion-col>
                                        <ion-row>
                                            <ion-col class="purple-purple-color">
                                                {{ cartList.supplier.name }}
                                            </ion-col>
                                        </ion-row>

                                        <ion-row>
                                            <ion-col class="fw-b" size="auto">
                                                {{ list.name }}
                                            </ion-col>

                                            <ion-col v-if="list.isDeleted">
                                                <highlight-box :color="HIGHLIGHT_COLOR.CORAL">
                                                    {{ $t('label.deleted') }}
                                                </highlight-box>
                                            </ion-col>
                                        </ion-row>
                                    </ion-col>

                                    <ion-col size="auto" v-if="userStore.permission[PERMISSION_KEY.CART_DELETE]">
                                        <button-delete @click="deleteList(list)" class="danger-ripple" />
                                    </ion-col>
                                </ion-row>

                                <div class="line grey4-grey4-background" />

                                <template v-for="listItem of list.cartItems" :key="listItem.id">
                                    <cart-details-sliding
                                        :entry="listItem"
                                        @amount="listItem.quantity = $event ?? 0; disableOnPatch($event, listItem.id)"
                                        @enable="resetDebounce(listItem.id)"
                                        :edit="userStore.permission[PERMISSION_KEY.CART_UPDATE] || userStore.permission[PERMISSION_KEY.CART_DELETE]"
                                    />
                                    <div class="line grey4-grey4-background ml-3" style="width: calc(100% - 2rem);" />
                                    <!-- // TODO: ADD ERROR FOR NO EMAIL-->
                                </template>
                            </template>

                            <ion-row v-if="cartList.cartItems.length" class="pt-1">
                                <ion-col class="center-y">
                                    <div class="ml-2 fw-b">{{ $t('messages.cart.noList') }}:</div>
                                </ion-col>
                                <ion-col size="auto">
                                    <button-delete
                                        @click="deleteNoListItems(cartList.supplier.id)" class="danger-ripple"
                                    />
                                </ion-col>
                            </ion-row>

                            <template
                                v-for="(noListItem, index) of cartList.cartItems.filter((i) => !i.orderId)"
                                :key="noListItem.id"
                            >
                                <cart-details-sliding
                                    :entry="noListItem"
                                    :edit="userStore.permission[PERMISSION_KEY.CART_UPDATE] || userStore.permission[PERMISSION_KEY.CART_DELETE]"
                                    :no-top="index === 0"
                                    @amount="noListItem.quantity = $event ?? 0; disableOnPatch($event, noListItem.id)"
                                    @enable="resetDebounce(noListItem.id)"
                                />
                            </template>

                            <ion-row v-if="cartList.cartItems.filter((i) => i.orderId).length" class="pt-1">
                                <ion-col class="center-y">
                                    <div class="ml-2 mt-3 mb-4 fw-b">{{ $t('messages.cart.lastOrder') }}:</div>
                                </ion-col>
                            </ion-row>

                            <template
                                v-for="(lastOrderItem, index) of cartList.cartItems.filter((i) => i.orderId)"
                                :key="lastOrderItem.id"
                            >
                                <cart-details-sliding
                                    :entry="lastOrderItem"
                                    :edit="userStore.permission[PERMISSION_KEY.CART_UPDATE] || userStore.permission[PERMISSION_KEY.CART_DELETE]"
                                    :no-top="index === 0"
                                    @amount="lastOrderItem.quantity = $event ?? 0; disableOnPatch($event, lastOrderItem.id)"
                                    @enable="resetDebounce(lastOrderItem.id)"
                                />
                            </template>

                            <ion-grid
                                v-if="!cartList.supplier.email && cartList.supplier.kind !== KIND.ONLINE"
                                style="color: darkgoldenrod;" class="grey4-grey4-background pb-1 flex-grow-0 max-width"
                            >
                                <ion-row>
                                    <ion-col size="2" class="center">
                                        <ion-icon class="fs-28" :icon="warningOutline" />
                                    </ion-col>
                                    <ion-col>
                                        {{ $t('messages.deliverer.noEmail') }}
                                    </ion-col>
                                </ion-row>

                                <ion-row v-if="userStore.permission[PERMISSION_KEY.SUPPLIER_CREATE]">
                                    <ion-col class="center">
                                        <u @click="goToSupplierSettings(cartList.supplier.id)">
                                            {{ $t('titleRoute.editLocationDeliverer') }}
                                        </u>
                                    </ion-col>
                                </ion-row>
                            </ion-grid>
                        </ion-grid>
                    </div>

                    <div slot="footer">
                        <ion-row class="grey4-grey4-background py-2 fs-smaller grey1-grey1-color">
                            <ion-col>
                                <ion-row>
                                    <ion-col>
                                        {{ $t('messages.cart.amountItems', { amount: orderAmountList(cartList) }) }}
                                    </ion-col>
                                </ion-row>

                                <ion-row>
                                    <ion-col class="py-0 center-y">
                                        {{ $t('messages.cart.amount', { amount: cartItemAmount(cartList) }) }}
                                    </ion-col>
                                </ion-row>
                            </ion-col>

                            <ion-col
                                v-if="userStore.permission[PERMISSION_KEY.ORDER_REQUEST]"
                                size="auto"
                                class="ion-justify-content-end d-flex ion-align-items-center"
                                @click="showDisableReason($event, cartList)"
                            >
                                <button-base
                                    :disabled="disableCartListOrder(cartList)"
                                    class="max-height m-0" @click="emit('next', cartList)"
                                >
                                    {{ $t('button.orderOverview') }}
                                </button-base>
                            </ion-col>
                        </ion-row>
                    </div>
                </ion-accordion>

                <ion-row class="grey4-grey4-background p-1 fs-smaller grey1-grey1-color">
                    <ion-col>
                        <ion-row>
                            <ion-col>
                                {{ $t('messages.cart.amountItems', { amount: orderAmountList(cartList) }) }}
                            </ion-col>
                        </ion-row>

                        <ion-row>
                            <ion-col class="py-0 center-y">
                                {{ $t('messages.cart.amount', { amount: cartItemAmount(cartList) }) }}
                            </ion-col>
                        </ion-row>
                    </ion-col>

                    <ion-col
                        v-if="userStore.permission[PERMISSION_KEY.ORDER_REQUEST]"
                        size="auto"
                        class="ion-justify-content-end d-flex ion-align-items-center"
                        @click="showDisableReason($event, cartList)"
                    >
                        <button-base
                            :disabled="disableCartListOrder(cartList)"
                            class="max-height m-0" @click="emit('next', cartList)"
                        >
                            {{ $t('button.orderOverview') }}
                        </button-base>
                    </ion-col>
                </ion-row>
            </div>
        </ion-accordion-group>

        <template v-else-if="!loading">
            <div class="purple-purple-color center-safe flex-d-c flex-grow-2">
                <div>{{ $t('messages.emptyCart') }}</div>
            </div>
        </template>
        <template v-else>
            <div class="max-size center">
                <ion-spinner color="medium" style="--color: var(--ion-color-purple)" />
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">import { ref as _ref } from 'vue';

import {
    IonAccordion,
    IonAccordionGroup,
    IonAvatar,
    IonCol,
    IonGrid,
    IonIcon,
    IonImg,
    IonItem,
    IonLabel,
    IonProgressBar,
    IonRow,
    IonSelectOption,
    IonSpinner,
    IonText,
    modalController,
    popoverController,
} from "@ionic/vue";
import { computed, onBeforeUnmount, onMounted, reactive, ref } from "vue";
import router, { ROUTE_NAME } from "@/router";
import { t } from "@/plugins/vue-i18n";
import { useDebounceFn } from "@vueuse/core";

import ButtonBase from "@/components/button/ButtonBase.vue";
import CartDetailsSliding from "@/components/various/CartDetailsSliding.vue";
import ButtonDelete from "@/components/button/ButtonDelete.vue";
import ScanModal from "@/components/modal/ScanModal.vue";
import SelectSimple from "@/components/selects/SelectSimple.vue";

import { useUserStore } from "@/stores/user";
import { useListStore } from "@/stores/lists";
import { useSupplierStore } from "@/stores/suppliers";
import { usePersistentStore } from "@/stores/persistent";
import { useNotificationStore } from "@/stores/notification";

import { popAlertConfirm } from "@/functions/alert";
import { interactionFeedbackToast } from "@/functions/baseToast";
import { isApp } from "@/functions/enviroment";

import { PERMISSION_KEY } from "@/types/user";
import { CartData, CartList, Supplier } from "@/types/supplier";
import { KIND } from "@/types/enum/supplierEnum";
import { SelectTouchEvent } from "@/types/frontend/event";

import { warningOutline } from "ionicons/icons"
import MessagePopover from "@/components/popover/MessagePopover.vue";
import { DateTime } from "luxon";
import { Keyboard } from "@capacitor/keyboard";
import { Capacitor, PluginListenerHandle } from "@capacitor/core";
import HighlightBox from "@/components/card/HighlightBox.vue";
import { HIGHLIGHT_COLOR, HIGHLIGHT_MESSAGE } from "@/types/enum/propEnum";
import { useLoader } from "@/composables/useLoader";
import LinkText from "@/components/textSnippet/LinkText.vue";
import { PROMOTION_TYPE } from "@/types/enum/notification";

defineProps<{ cartData: CartData }>()
const emit = defineEmits<{ (e: 'next', value: CartData): void }>()

const userStore = useUserStore()
const listStore = useListStore()
const supplierStore = useSupplierStore()
const persistentStore = usePersistentStore()
const notificationStore = useNotificationStore()

const { showLoading, hideLoading } = useLoader()

let loading = _ref(false)
const locationId = _ref(router.currentRoute.value.params.locationId as string)
const scanSupplier = ref<Supplier | undefined>(undefined)
const debounceChangeAmount = ref<boolean>(false)
const openList = ref<string>(listStore.cartLists.length === 1 ? listStore.cartLists[0]?.supplier?.id : '')
let currentItem = reactive<{ id: string; amount: number }>({ id: '', amount: 0 })
const containsExpired = (list: CartData): boolean => {
    for (const item of list.cartItems) {
        if (!item.promotion || !item.promotion.validUntil) continue
        if (DateTime.fromISO(item.promotion.validUntil).diffNow('minutes').minutes < 0) return true
    }
    return false
}

const toggleAccordion = (supplierId: string): void => {
    if (openList.value === supplierId) {
        openList.value = ''
        return
    }
    openList.value = supplierId
}

const disableCartListOrder = (cartList: CartData): boolean => {
    return !userStore.sessionInfo.emailConfirmed
        || (!cartList.supplier.email && cartList.supplier.kind !== KIND.ONLINE)
        || debounceChangeAmount.value
        || !userStore.permission[PERMISSION_KEY.ORDER_CREATE]
        || containsExpired(cartList)
        || persistentStore.noConnection
}

// const locationId = computed<string>(() => String(router.currentRoute.value.params.locationId))
const isScanAvailable = computed<boolean>(() => userStore.permission[PERMISSION_KEY.CART_ADD] && !persistentStore.offlineMode && isApp() && !!supplierStore.allSuppliers.find(supplier => supplier.scannerActivated))
// const orderedLists = computed<Array<List>>(() => {
//     const lists = listStore.lists?.data as Array<List>;
//     return lists?.length > 1
//         ? lists.reduce((slicedList: Array<List>, list: List) => {
//             if (!slicedList.find(({ supplierId }) => supplierId === list.supplierId)) slicedList.push(list)
//             return slicedList;
//         }, [])
//         : lists ?? [];
// })

const showPromoPill = (cartList: CartData): boolean => {
    return cartList.summary.some((i) => {
        const promotion = i.item.promotion
        const product = notificationStore.findPromotionProduct(promotion?.id, promotion?.productId)

        return i.isPromotion || (i.item.promotion?.promotionType !== PROMOTION_TYPE.BONUS_FLEXIBLE && Number(i.quantity) < Number(product?.stepAmount))
    })
}

const cartItemAmount = (item: CartData): void | number => {
    return item.supplier?.totalCartPosition
}

const orderAmountList = (item: CartData): number => {
    return item.supplier?.totalCartItems
}

const resetDebounce = (itemId: string) => {
    debounceChangeAmount.value = false
    if (itemId === currentItem.id) currentItem = {
        id: '',
        amount: 0
    }
}

const disableOnPatch = async (amount: any, itemId: string): Promise<void> => {
    if (!currentItem) {
        currentItem = { id: itemId, amount }
    }
    if (currentItem.id && currentItem.id !== itemId) {
        patchItem(currentItem.amount, currentItem.id)
        currentItem = { id: itemId, amount }
    }
    debounceChangeAmount.value = true
    await debouncePatchItem(amount, itemId)
}

const patchItem = async (amount: number, itemId: string): Promise<void> => {
    if (!amount) return

    const response = await listStore.patchCartItem(locationId.value, itemId, amount)

    if (!response) return

    await listStore.getShoppingCart(locationId.value)

    resetDebounce(itemId)
}

const debouncePatchItem = useDebounceFn(patchItem, 500)

const deleteList = async (list: CartList): Promise<void> => {
    popAlertConfirm({
        message: t('alert.delete.cartList', { target: list.name }),
        handler: {
            ok: async () => {
                const response = await listStore.deleteCartList(locationId.value, list.id)

                if (response) {
                    await listStore.getShoppingCart(locationId.value)
                }
            }
        }
    })
}

const deleteNoListItems = async (supplierId: string): Promise<void> => {
    popAlertConfirm({
        message: t('alert.delete.noList'),
        handler: {
            ok: async () => {
                await showLoading()
                await listStore.deleteNoListItems(locationId.value, supplierId)
                setTimeout(async () => {
                    await listStore.getShoppingCart(locationId.value)
                    await hideLoading()
                }, 250)
            }
        }
    })
}

const deleteCart = async (cartData: CartData) => {
    await showLoading()
    await listStore.deleteFullCart(locationId.value, cartData.supplier.id)

    setTimeout(async () => {
        await listStore.getShoppingCart(locationId.value)
        await hideLoading()
    }, 250)
}

const deleteCartCheck = (cartData: CartData) => {
    popAlertConfirm({
        message: t('alert.delete.cartSupplier', { target: cartData.supplier.name }),
        handler: {
            ok: () => deleteCart(cartData)
        }
    })
}

const goToSupplierSettings = (supplierId: string): void => {
    if (!supplierId) return
    router.push({ name: ROUTE_NAME.EDIT_CUSTOM_SUPPLIER, params: { supplierId: supplierId } })
    modalController.dismiss()
}

const resendMail = async (): Promise<void> => {
    const response = await userStore.resendConfirmationMail()

    if (response) await interactionFeedbackToast(true, {
        type: 'resendMail',
        id: 'resend-register-mail'
    })
}

const chooseListId = (): void => {
    // use querySelector, because if we take from ref open() method doesn't work
    const listSelect: SelectTouchEvent | null = document.querySelector('#list-select')

    if (listSelect) listSelect.open()
}
const openScanModel = async (): Promise<void> => {
    const modal = await modalController.create({
        component: ScanModal,
        componentProps: {
            supplierId: scanSupplier.value?.id,
        },
        cssClass: 'scanner-modal'
    })
    scanSupplier.value = undefined
    modal.onDidDismiss()
    await modal.present()
}
const getSupplierName = (supplierId: string): string | undefined => {
    return supplierStore.allSuppliers.find(supplier => supplier.id === supplierId)?.name
}

const showDisableReason = async (event: MouseEvent | TouchEvent, cartList: CartData) => {
    let popoverElement: HTMLIonPopoverElement | undefined = undefined

    if (!userStore.permission[PERMISSION_KEY.ORDER_CREATE]) popoverElement = await popoverController.create({
        component: MessagePopover,
        componentProps: {
            message: t('messages.missingCartOrderPermission')
        },
        arrow: true,
        cssClass: 'message-popover w-500px',
        showBackdrop: false,
        alignment: 'end',
        side: 'bottom',

        event
    })

    if (containsExpired(cartList)) popoverElement = await popoverController.create({
        component: MessagePopover,
        componentProps: {
            message: t('messages.containsExpiredPromotion')
        },
        arrow: true,
        cssClass: 'message-popover w-500px',
        showBackdrop: false,
        alignment: 'end',
        side: 'bottom',

        event
    })

    if (!popoverElement) return

    await popoverElement.present()

    setTimeout(() => {
        if (!popoverElement) return
        popoverElement.dismiss()
    }, 5000)
}

let keyboardListener: PluginListenerHandle | null = null
if (Capacitor.isNativePlatform()) {
    keyboardListener = Keyboard.addListener('keyboardDidShow', () => {
        window.addEventListener('click', (ev) => {
            ev.preventDefault()
            ev.stopPropagation()
            Keyboard.hide()
            document.activeElement.blur()
        }, { once: true })
    })
}

onBeforeUnmount(() => {
    keyboardListener?.remove()
    keyboardListener = null
})

onMounted(async () => {
    loading.value = true
    await Promise.all([
        notificationStore.getPromotions({ locationId: locationId.value }),
        listStore.getShoppingCart(locationId.value),
        userStore.getMe(),
        supplierStore.getLocationDeliveryDays(locationId.value)
    ])
    loading.value = false
})
</script>

<style>
.scan-icon {
    height: 20px;
}

.cart:first-child {
    margin-top: 0;
}

.cart-header {
    --min-height: 71px;
}

.cart-header > .ion-accordion-toggle-icon {
    color: var(--ion-color-grey1);
}

.empty-cart-icon {
    width: 50vw;
    height: 50vw;
    max-width: 300px;
    max-height: 300px;
}

#list-select {
    display: none;
}
</style>
